/**
 * paymentService.js
 * ------------------
 * Uses Firebase ID token for auth.
 * Calls the local-api Express server (port 5050) via React proxy.
 */

import { auth } from '../firebase/firebaseConfig';

/**
 * Gets a fresh Firebase ID token for the current user.
 * Throws if not logged in.
 */
async function getFirebaseToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('לא מחובר — אנא התחבר לפני ביצוע תשלום');
  return user.getIdToken(/* forceRefresh= */ false);
}

/**
 * Shared fetch helper with Firebase auth header.
 */
async function authFetch(url, options = {}) {
  const token = await getFirebaseToken();
  let res;
  try {
    res = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  } catch (networkErr) {
    throw new Error('שרת התשלומים לא זמין כרגע. נסה שוב בעוד רגע.');
  }

  // The CRA dev proxy returns plain text ("Proxy error: ...") when the
  // local-api Express server isn't running — guard JSON parsing for that case.
  const raw = await res.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    // Not JSON — likely "Proxy error..." from CRA or an HTML error page
    const friendly = /Proxy error/i.test(raw)
      ? 'שרת התשלומים המקומי לא רץ. אנא ודא שה-Local API פעיל (npm start בתיקיית local-api).'
      : `השרת החזיר תגובה לא תקינה (${res.status}). נסה שוב בעוד רגע.`;
    throw new Error(friendly);
  }

  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
  }
  return data;
}

/**
 * Create a Stripe PaymentIntent on the server.
 */
export async function createStripePaymentIntent(amount, credits, currency = 'USD') {
  return authFetch('/api/payments/stripe/create', {
    method: 'POST',
    body: JSON.stringify({ amount, credits, currency }),
  });
}

/**
 * Notify the server that a successful payment was made and award credits.
 */
export async function completePurchaseOnServer({ credits, amount, provider, paymentIntentId }) {
  return authFetch('/api/purchases/complete', {
    method: 'POST',
    body: JSON.stringify({ credits, amount, provider, paymentIntentId }),
  });
}

/**
 * Create a NOWPayments crypto payment (returns unique address per order).
 */
export async function createCryptoPayment({ amount, credits, pay_currency }) {
  return authFetch('/api/payments/crypto/create', {
    method: 'POST',
    body: JSON.stringify({ amount, credits, currency: 'usd', pay_currency }),
  });
}

/**
 * Poll payment status from NOWPayments via our backend.
 */
export async function getCryptoPaymentStatus(payment_id) {
  return authFetch(`/api/payments/crypto/status/${payment_id}`);
}

/**
 * Create a PayPlus hosted payment page (Israeli payment gateway).
 * Returns { uuid, paymentUrl, ... } — client opens paymentUrl in popup/new tab.
 */
export async function createPayPlusPayment({ amount, credits, currency = 'ILS' }) {
  return authFetch('/api/payments/payplus/create', {
    method: 'POST',
    body: JSON.stringify({ amount, credits, currency }),
  });
}

/**
 * Poll PayPlus payment status by our internal uuid.
 * Returns { status: 'pending'|'succeeded'|'failed', credits, amount, currency }
 */
export async function getPayPlusStatus(uuid) {
  return authFetch(`/api/payments/payplus/status/${uuid}`);
}

/**
 * Create a UPay payment intent (Israeli gateway, NIS only).
 * Returns { uuid, action, fields, amount, currency, credits } —
 * client builds an HTML form with all `fields` and submits to `action`.
 */
export async function createUpayPayment({ amount, credits }) {
  return authFetch('/api/payments/upay/create', {
    method: 'POST',
    body: JSON.stringify({ amount, credits, currency: 'ILS' }),
  });
}

/**
 * Poll UPay payment status by our internal uuid.
 * Returns { status: 'pending'|'succeeded'|'failed', credits, amount, currency }
 */
export async function getUpayStatus(uuid) {
  return authFetch(`/api/payments/upay/status/${uuid}`);
}
